import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from "react";
import {
  Button,
  FormControl,
  ButtonToolbar,
  InputGroup,
  Row,
  Col,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Lens, TooltipInfoIcon, DownloadFile } from "@icons";
import {
  getTranslations,
  getInterventions,
  getFinancings,
  getStructureClusters,
  exportExcel,
  getMonitoringGroups,
} from "@lib/api";
import { AgGridReact } from "ag-grid-react/lib/agGridReact";
import "@ag-grid-community/core/dist/styles/ag-grid.css";
import "@ag-grid-community/core/dist/styles/ag-theme-alpine.css";
import AG_GRID_LOCALE_IT from "@components/projects/ag-grid-translate/locale-it";
import ActionBtnRenderer from "@components/projects/actionBtnRenderer/ActionBtnRenderer";
import CustomModal from "components/CustomModal";
import HomeDonutChart from "components/charts/homeDonutChart";
import { formatValue, mergeAvgAndStdDev } from "@lib/helpers/homepageHelpers";
import { date } from "@lib/helpers/index";
import MonitoringProjectsCTA from "./MonitoringProjectsCTA";
import { MetricsTable } from "components/metricsTable/metricsTable";
import {
  exportCreatedMonitoringExcel,
  getMonitoringProjects,
  getMonitoringStats,
} from "@lib/api/index";

// Il monitoraggio più recente PER DATA. Non si può prendere il primo della lista:
// il backend la ordina per monitoring_group decrescente, che è un'altra cosa (il
// gruppo 7 può essere di gennaio e il 3 di settembre). Serve solo alla
// preselezione, l'ordine con cui la lista viene mostrata non cambia.
//
// La data arriva come "2026-09-30 09:00:00": va portata in ISO prima di parsarla,
// altrimenti Safari restituisce Invalid Date e, siccome NaN non è mai maggiore di
// NaN, il confronto ricadrebbe silenziosamente sul primo elemento della lista.
//
// Definite fuori dal componente: dentro verrebbero ricreate a ogni render e
// l'effetto che le usa rifarebbe la fetch all'infinito.
const monitoringTime = (g) => {
  const t = new Date(String(g?.date ?? '').replace(' ', 'T')).getTime();
  return Number.isNaN(t) ? -Infinity : t;
};

const mostRecentMonitoring = (groups) =>
  (groups ?? []).reduce(
    (latest, g) => (!latest || monitoringTime(g) > monitoringTime(latest) ? g : latest),
    null,
  );

const Monitoring = (props) => {
  const [translations, setTranslations] = useState([]);
  const [projects, setProjects] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [financing, setFinancing] = useState([]);
  const [cluster, setCluster] = useState([]);
  const [optionsPagination, setOptionsPagination] = useState([]);
  const [dataRating, setDataRating] = useState(0);
  const [scoreEsg, setScoreEsg] = useState("");
  const [noOutData, setNoOutData] = useState([]);
  const [availableMonitoring, setAvailableMonitoring] = useState([]);
  const [selectedMonitoring, setSelectedMonitoring] = useState(null);
  const gridRef = useRef();
  const [refresh, setRefresh] = useState(false);
  const localeText = AG_GRID_LOCALE_IT;

  const [isLoading, setIsLoading] = useState(false);
  const [isAvailableMonitoringLoading, setIsAvailableMonitoringLoading] =
    useState(false);
  //STATS MONITORAGGIO
  const [monitoringStats, setMonitoringStats] = useState({});
  const [monitoringDataRating, setMonitoringDataRating] = useState(0);
  const [monitoringScoreEsg, setMonitoringScoreEsg] = useState("");
  const [monitoringNoOutData, setMonitoringNoOutData] = useState([]);
  const isInMonitoring = props.location.pathname.indexOf("monitoraggio") > -1;
  console.log("isInMonitoring", selectedMonitoring);
  useEffect(() => {
    getTranslations().then((retrievedTranslation) => {
      setTranslations(retrievedTranslation);
    });

    getInterventions().then((retrievedInterventions) => {
      setInterventions(retrievedInterventions);
    });

    getFinancings().then((retrievedFinancings) => {
      setFinancing(retrievedFinancings);
    });

    getStructureClusters().then((retrievedCluster) => {
      setCluster(retrievedCluster);
    });
  }, [projects]);

  useEffect(() => {
    setIsAvailableMonitoringLoading(true);
    getMonitoringGroups().then((retrievedMonitoringGroups) => {
      setAvailableMonitoring(retrievedMonitoringGroups);
      setIsAvailableMonitoringLoading(false);

      // All'atterraggio si apre già sull'ultimo monitoraggio effettuato, così la
      // lista dei progetti e i valori sono visibili senza altri click. Si imposta
      // solo se l'utente non ha ancora scelto, per non sovrascrivere la sua scelta
      // quando la lista viene ricaricata dopo aver avviato un monitoraggio.
      const latest = mostRecentMonitoring(retrievedMonitoringGroups);
      if (latest) setSelectedMonitoring((current) => current ?? latest.id);
    });
  }, []);

  useEffect(() => {
    if (selectedMonitoring) {
      getMonitoringProjects(selectedMonitoring).then((retrievedProjects) => {
        setProjects(retrievedProjects);
      });

      // Una sola chiamata alimenta entrambe le colonne: /monitoringStats ritorna `pre`
      // (portafoglio con i progetti monitorati ai valori ex-ante) e `post` (valori
      // correnti), calcolati con lo stesso metodo. Prendere il "pre" da /v1/stats
      // confrontava numeri non omogenei: quella pipeline usa aggregati in cache e
      // perimetri propri, e mostrava uno scostamento anche senza monitoraggi inviati.
      getMonitoringStats(selectedMonitoring).then((retrievedStats) => {
        if (!retrievedStats || Object.keys(retrievedStats).length === 0) return;

        const pre = retrievedStats.pre ?? retrievedStats;
        const post = retrievedStats.post ?? retrievedStats;

        setMonitoringStats(retrievedStats);

        // Proponent data con deviazione standard
        setNoOutData(
          mergeAvgAndStdDev(
            pre.avgProponentSroiNoOut,
            pre.stdDevSroiProponentNoOut,
          ),
        );
        setMonitoringNoOutData(
          mergeAvgAndStdDev(
            post.avgProponentSroiNoOut,
            post.stdDevSroiProponentNoOut,
          ),
        );

        // ESG Score e rating: donut pre e post
        setScoreEsg(formatValue(pre.avgPortfolioValue));
        setDataRating(pre.esgOutlook?.percentage || 0);
        setMonitoringScoreEsg(formatValue(post.avgPortfolioValue));
        setMonitoringDataRating(post.esgOutlook?.percentage || 0);
      });
    }

    // // useffect cleanup
    // return () => {
    //   setSelectedMonitoring(null);
    // };
  }, [selectedMonitoring]);

  const sroiDetailRows = [
    // Benefici economici
    {
      id: "avgVanBenefits",
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === "AVGVANBENEFITS")
            .map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === "AVGVANBENEFITS")
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement="right"
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id="avgvanbenefits-tooltip">
                  {translations
                    .filter((trans) => trans.code === "VANBENEFITS")
                    .map((trans) => trans.description)}
                </Tooltip>
              }
            >
              <TooltipInfoIcon className="tooltip-ico mb-1" />
            </OverlayTrigger>
          )}
        </>
      ),
      preMonitoring: monitoringStats?.pre?.totalBenefits
        ? `${monitoringStats.pre.totalBenefits.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
      postMonitoring: monitoringStats?.post?.totalBenefits
        ? `${monitoringStats.post.totalBenefits.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
    },

    // Costi economici
    {
      id: "avgVanCosts",
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === "AVGVANCOSTS")
            .map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === "AVGVANCOSTS")
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement="right"
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id="avgvancosts-tooltip">
                  {translations
                    .filter((trans) => trans.code === "VANCOSTS")
                    .map((trans) => trans.description)}
                </Tooltip>
              }
            >
              <TooltipInfoIcon className="tooltip-ico mb-1" />
            </OverlayTrigger>
          )}
        </>
      ),
      preMonitoring: monitoringStats?.pre?.totalCosts
        ? `${monitoringStats.pre.totalCosts.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
      postMonitoring: monitoringStats?.post?.totalCosts
        ? `${monitoringStats.post.totalCosts.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
    },

    // VANE
    {
      id: "avgVane",
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === "AVGVANE")
            .map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === "AVGVANE")
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement="right"
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id="avgvane-tooltip">
                  {translations
                    .filter((trans) => trans.code === "VANE")
                    .map((trans) => trans.description)}
                </Tooltip>
              }
            >
              <TooltipInfoIcon className="tooltip-ico mb-1" />
            </OverlayTrigger>
          )}
        </>
      ),
      preMonitoring: monitoringStats?.pre?.totalVANE
        ? `${monitoringStats.pre.totalVANE.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
      postMonitoring: monitoringStats?.post?.totalVANE
        ? `${monitoringStats.post.totalVANE.toLocaleString("it-IT", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} €`
        : "N/A",
    },

    // SROI medio
    {
      id: "avgSroi",
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === "AVGSROI")
            .map((trans) => trans.text)}

          {/* tooltip nero con ICS132/ICS133 */}
          <OverlayTrigger
            placement="right"
            delay={{ show: 200, hide: 400 }}
            overlay={
              <div
                className="d-flex p-3 f-500"
                style={{
                  backgroundColor: "rgba(0,0,0,0.9)",
                  color: "#ffffff",
                  fontSize: ".9rem",
                  borderRadius: "5px",
                }}
              >
                <span className="mr-2">
                  {translations
                    .filter((trans) => trans.code === "ICS132")
                    .map((trans) => trans.text)}
                </span>
                <span>
                  ({" "}
                  {translations
                    .filter((trans) => trans.code === "ICS133")
                    .map((trans) => trans.text)}{" "}
                  )
                </span>
              </div>
            }
          >
            <TooltipInfoIcon className="tooltip-ico mb-1" />
          </OverlayTrigger>

          {/* tooltip descrizione AVGSROI, se c'è */}
          {translations
            .filter((trans) => trans.code === "AVGSROI")
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement="right"
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id="avgsroi-tooltip">
                  {translations
                    .filter((trans) => trans.code === "AVGSROI")
                    .map((trans) => trans.description)}
                </Tooltip>
              }
            >
              <TooltipInfoIcon className="tooltip-ico mb-1" />
            </OverlayTrigger>
          )}
        </>
      ),
      preMonitoring: (
        <>
          {monitoringStats?.pre?.avgSroi
            ? monitoringStats.pre.avgSroi.toLocaleString("it-IT", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : "N/A"}
          <span className="ml-2 f-300">
            {noOutData &&
              noOutData
                .filter((el) => el.proponent === "portfolio")
                .map(
                  (el) =>
                    `(${el.avgValue.toLocaleString("it-IT", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
      postMonitoring: (
        <>
          {monitoringStats?.post?.avgSroi
            ? monitoringStats.post.avgSroi.toLocaleString("it-IT", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : "N/A"}
          <span className="ml-2 f-300">
            {monitoringNoOutData &&
              monitoringNoOutData
                .filter((el) => el.proponent === "portfolio")
                .map(
                  (el) =>
                    `(${el.avgValue.toLocaleString("it-IT", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
    },
  ];

  useEffect(() => {
    setOptionsPagination([
      <Fragment key="options">
        <option defaultValue="20" key="20">
          20
        </option>
        <option value="50" key="50">
          50
        </option>
        <option value="100" key="100">
          100
        </option>
      </Fragment>,
    ]);
  }, []);

  useEffect(() => {
    if (refresh) {
      // TODO: Update with the new API for the monitoring projects
      getMonitoringGroups().then((retrievedMonitoringGroups) => {
        setAvailableMonitoring(retrievedMonitoringGroups);
      });
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (isLoading) {
      exportExcel().then(() => {});
      setIsLoading(false);
    }
  }, [isLoading]);

  const onFilterTextBoxChanged = useCallback((e) => {
    gridRef.current.api.setQuickFilter(e.target.value);
  }, []);

  // const onGridReady = useCallback(() => {
  //   // TODO: Update with the new API for the monitoring projects
  //   getProjects().then((retrievedProjects) => {
  //     //TEST
  //     // setProjects(retrievedProjects.filter((el) => el?.ver > 0));
  //     setProjects(retrievedProjects);
  //   });
  // }, []);

  const autoSizeAll = useCallback((skipHeader) => {
    const allColumnIds = [];
    gridRef.current.columnApi.getAllColumns().forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef.current.columnApi.autoSizeColumns(allColumnIds, skipHeader);
  }, []);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (window.innerWidth >= 2540) {
        //&& window.screen.height >= 768
        // Resolution is 3840 //x768 or above
        gridRef.current.api.sizeColumnsToFit();
      } else {
        autoSizeAll(false);
      }
    },
    [autoSizeAll],
  );

  const onModelUpdated = useCallback(
    (params) => {
      if (window.innerWidth >= 2540) {
        //&& window.screen.height >= 768
        // Resolution is 3840 //x768 or above
        gridRef.current.api.sizeColumnsToFit();
      } else {
        autoSizeAll(false);
      }
    },
    [autoSizeAll],
  );

  const onPageSizeChanged = useCallback(() => {
    let value = document.getElementById("page-size").value;
    gridRef.current.api.paginationSetPageSize(Number(value));
  }, []);

  const jsxInCell = (params) => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        {params.data.requestId}{" "}
        <TooltipInfoIcon style={{ width: "1rem", minWidth: "1rem" }} />
      </div>
    );
  };

  const defaultColDef = {
    resizable: true,
    wrapText: true,
    autoHeight: true,
    flex: 1,
    minWidth: 100,
    suppressMovable: true,
    cellStyle: { padding: "0 0.7rem" },
  };

  const columnDefs = [
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS002")
        .map((trans) => {
          return trans.text;
        }),
      field: "clientId",
      filter: "agTextColumnFilter",
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS003")
        .map((trans) => trans.text),
      field: "requestId",
      filter: "agTextColumnFilter",
      tooltipField: "description",
      //tooltipComponentParams: { color: 'red' }, //not work
      cellRendererSelector: () => {
        const idAndSvg = {
          component: jsxInCell,
        };

        return idAndSvg;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS005")
        .map((trans) => trans.text),
      field: "proponents",
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.proponents.name;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS033")
        .map((trans) => trans.text),
      field: "interventionClassCode",
      filter: "agTextColumnFilter",
      //minWidth: 200,
      valueGetter: (params) => {
        // Tipo di finanziamento reale (Ristrutturazione, Acquisto nuove
        // attrezzature, ...); fallback alla classe di intervento SROI quando
        // il finanziamento non è valorizzato. Coerente con il DB Excel.
        const interventionName = params.data.financingTypeCode
          ? financing
              .filter(
                (financingItem) =>
                  financingItem.code === params.data.financingTypeCode,
              )
              .map((filteredFinancing) =>
                (filteredFinancing.text || "").toUpperCase(),
              )
          : interventions
              .filter(
                (intervention) =>
                  intervention.code === params.data.interventionClassCode,
              )
              .map((filteredIntervention) =>
                (filteredIntervention.text || "").toUpperCase(),
              );

        return interventionName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS008")
        .map((trans) => trans.text),
      field: "sector",
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        const sectorName = params.data.structureClusterCode
          ? cluster
              .filter(
                (cluster) => cluster.code === params.data.structureClusterCode,
              )
              .map(
                (filteredCluster) =>
                  `${params.data.sectors.text} - ${filteredCluster.cluster}`,
              )
          : params.data.sectors.text;
        return sectorName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS009")
        .map((trans) => trans.text),
      field: "regions",
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.locations.nomeRegione;
      },
    },

    {
      headerName: translations
        .filter((trans) => trans.code === "ICS095")
        .map((trans) => trans.text),
      field: "sroiratings",
      cellStyle: { textAlign: "right" },
      valueGetter: (params) => {
        if (params.data.interventionClassCode !== "NOINTPURCH") {
          const totalCapex = params.data.sroiratings
            .filter((esgrating) => esgrating.code === "TOTALCAPEX")
            .map((filteredRating) =>
              filteredRating.value.toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
          return totalCapex.length > 0 ? `${totalCapex} €` : "";
        } else {
          return params.data?.totalInvestment
            ? `${params.data.totalInvestment.toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} €`
            : "";
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS053")
        .map((trans) => trans.text),
      field: "esgratings",
      cellStyle: { textAlign: "center" },
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals"],
        defaultOption: "equals",
      },
      valueGetter: (params) => {
        const rating = params.data.esgratings
          .filter((esgrating) => esgrating.type === "total")
          .map((filteredRating) => filteredRating.rating);

        return rating;
      },
    },

    {
      headerName: "Rating ESG (Monitoraggio)",
      field: "esgratingsmonitoring",
      cellStyle: { textAlign: "center" },
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals"],
        defaultOption: "equals",
      },
      valueGetter: (params) => {
        const rating = params.data.esgratings
          .filter((esgrating) => esgrating.type === "total")
          .map((filteredRating) => filteredRating.rating);

        return rating;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS096")
        .map((trans) => trans.text),
      field: "sroiratings",
      filter: "agTextColumnFilter",
      cellStyle: { textAlign: "right" },
      valueGetter: (params) => {
        // console.log('PARAMS:', params.data.sroiratings.find((sroi) => sroi.code === 'SROI')?.value);
        if (params.data.interventionClassCode !== "NOINTPURCH") {
          return params.data.sroiratings
            .filter((sroi) => sroi.code === "SROI")
            ?.map((filteredValue) =>
              filteredValue.value.toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
        } else {
          return "N.A.";
        }
      },
    },
    {
      headerName: "SROI (Monitoraggio)",
      field: "sroiratingsmonitoring",
      filter: "agTextColumnFilter",
      cellStyle: { textAlign: "right" },
      valueGetter: (params) => {
        // console.log('PARAMS:', params.data.sroiratings.find((sroi) => sroi.code === 'SROI')?.value);
        if (params.data.interventionClassCode !== "NOINTPURCH") {
          return params.data.sroiratings
            .filter((sroi) => sroi.code === "SROI")
            ?.map((filteredValue) =>
              filteredValue.value.toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
        } else {
          return "N.A.";
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === "ICS150")
        .map((trans) => trans.text),
      field: "lastMonitoring",
      filter: "agTextColumnFilter",
      cellStyle: { textAlign: "center" },
      valueGetter: (params) => {
        if (params.data?.lastMonitoring) {
          return date(params.data.lastMonitoring);
        } else {
          return "N.A.";
        }
      },
    },
    {
      /*PINNATO A DESTRA*/
      headerName: translations
        .filter((trans) => trans.code === "ICS052")
        .map((trans) => trans.text),
      field: "status",
      cellStyle: (params) =>
        params.data?.ver > 0 && !!!params.data?.isMonitoring
          ? { color: "#08a2aa", fontWeight: "bold", padding: "0 0.7rem" }
          : { color: "black", padding: "0 0.7rem" },
      pinned: "right",
      filter: "agTextColumnFilter",
      // minWidth: 100,
      valueGetter: (params) => {
        // Il BE calcola lo stato sullo snapshot di invio della versione corrente
        // (monitoringStatus): "Completato" solo se questionario e SROI sono stati
        // davvero ri-sottomessi. Il fallback copre i payload senza quel campo.
        if (params.data?.monitoringStatus) {
          return params.data.monitoringStatus;
        }
        const version = params.data?.ver;
        const isMonitoring = !!params.data?.isMonitoring;
        return version > 0 && !isMonitoring ? "Completato" : "Bozza";
      },
    },
    {
      headerName: "",
      pinned: "right",
      minWidth: 170,
      maxWidth: 200,
      cellStyle: { textAlign: "center" },
      cellRenderer: ActionBtnRenderer,
      cellRendererParams: (params) => ({
        setRefresh: setRefresh,
        isInMonitoring: isInMonitoring,
        isMonitoring: !!params.data?.isMonitoring,
        monitoringVersion: params.data?.ver ?? 1, // Recupera l'ID dal dato della riga
      }),
    },
  ];

  const downloadTooltip = (props) => (
    <Tooltip {...props}>Download Excel</Tooltip>
  );

  return (
    <>
      <div className="dashboard">
        <Row className="report-first gap-3 p-0 d-flex ">
          <Col className="simple-box report-1 mt-0">
            <h2 className="mb-0">
              {availableMonitoring.length === 0
                ? "Nessun progetto ancora inserito in monitoraggio. Usa la funzionalità in basso per avviarne uno."
                : "Seleziona un monitoraggio per vedere i dettagli o avviane uno nuovo."}
            </h2>
            <ButtonToolbar className="d-flex justify-content-center align-items-center gap-1">
              <CustomModal
                triggerLabel="Avvia un monitoraggio"
                title="Avvia un monitoraggio"
                body={<MonitoringProjectsCTA />}
                buttons={[
                  {
                    label: "Chiudi",
                    variant: "success",
                  },
                  // {
                  //   label: 'Conferma',
                  //   variant: 'primary',
                  //   onClick: () => alert('Azione confermata!'),
                  //   // closeOnClick: true (default)
                  // },
                ]}
                onCloseAction={() => setRefresh(true)}
              />
            </ButtonToolbar>
            <div>
              {isAvailableMonitoringLoading ? (
                <div className="d-flex justify-content-center align-items-center p-4">
                  <Spinner animation="border" role="status" className="mt-5">
                    <span style={{ visibility: "hidden" }}>Loading...</span>
                  </Spinner>
                </div>
              ) : (
                availableMonitoring.length > 0 && (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <h5 className="mt-4">Monitoraggi disponibili:</h5>
                    <ul>
                      {availableMonitoring.map((monitoring) => (
                        <li key={`${monitoring.id}-${monitoring.name}`}>
                          <Button
                            variant="link"
                            onClick={() => setSelectedMonitoring(monitoring.id)}
                          >
                            {monitoring.name} - Ultimo aggiornamento:{" "}
                            {new Date(monitoring.date).toLocaleDateString(
                              "it-IT",
                            )}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>
          </Col>
        </Row>
      </div>
      {projects && projects.length > 0 && (
        <div className="dashboard">
          <Row className="simple-box">
            <div className="d-flex justify-content-center align-items-center text-center w-100">
              <div className="p-3">
                <p className="text-capitalize">numerosità</p>
                <p className="my-4 font-lg-2">
                  {/* {stats.projectsSubmitted} */}
                  {projects.length}
                </p>
                <p className="text-uppercase f-500 min-label-width">
                  progetti monitorati
                </p>
              </div>
            </div>
            <Row className="report-first gap-3 p-0 d-flex flex-wrap">
              {/* secondo blocco sinistra */}
              <Col
                className="justify-content-center align-items-center text-center"
                style={{ minWidth: "400px" }}
              >
                <Row className="ml-0">
                  {/*TODO*/}
                  <h5 className="title-simple-box text-uppercase mt-1 pl-0">
                    {translations
                      .filter((trans) => trans.code === "ICS126")
                      .map((trans) => trans.text)}
                  </h5>
                </Row>

                {dataRating ? (
                  <Row className="mt-5 text-center d-flex gap-4">
                    <Col id="donut-ratingEsg" className="px-5">
                      <div className="d-flex flex-column flex-xl-row justify-content-center align-items-center">
                        <div className="flex-shrink-0">
                          <HomeDonutChart
                            chartId="ratingEsg"
                            data={dataRating}
                            scoreEsg={scoreEsg}
                            // scoreEsgNoOut={avgData
                            //   ?.filter((el) => el.name === 'Score Medio')
                            //   .map((esg) => esg.ESG)}
                            innerRadius={40}
                            outerRadius={60}
                          />
                        </div>
                        <p className="text-uppercase f-500 min-label-width mt-4 position-relative monitoring-esg-label">
                          {translations
                            .filter((trans) => trans.code === "ICS045")
                            .map((trans) => trans.text)}{" "}
                          pre-monitoraggio
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 200, hide: 400 }}
                            overlay={
                              <Tooltip id="button-tooltip">
                                {translations
                                  .filter((trans) => trans.code === "ICS045")
                                  .map((trans) => trans.description)}
                              </Tooltip>
                            }
                          >
                            <TooltipInfoIcon
                              className="tooltip-ico"
                              style={{
                                width: "1rem",
                                position: "absolute",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            />
                          </OverlayTrigger>
                        </p>
                      </div>
                      <div className="d-flex justify-content-center align-items-center flex-column flex-xl-row ">
                        <div className="flex-shrink-0">
                          <HomeDonutChart
                            chartId="comparedRatingEsg"
                            data={monitoringDataRating}
                            scoreEsg={monitoringScoreEsg}
                            innerRadius={40}
                            outerRadius={60}
                          />
                        </div>
                        <p className="text-uppercase f-500 min-label-width mt-4 position-relative monitoring-esg-label">
                          {translations
                            .filter((trans) => trans.code === "ICS045")
                            .map((trans) => trans.text)}{" "}
                          post-monitoraggio
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 200, hide: 400 }}
                            overlay={
                              <Tooltip id="button-tooltip">
                                {translations
                                  .filter((trans) => trans.code === "ICS045")
                                  .map((trans) => trans.description)}
                              </Tooltip>
                            }
                          >
                            <TooltipInfoIcon
                              className="tooltip-ico"
                              style={{
                                width: "1rem",
                                position: "absolute",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            />
                          </OverlayTrigger>
                        </p>
                      </div>
                    </Col>

                    <Col id="donut-legend" className="align-self-center">
                      <Row className="flex-column align-content-center">
                        <Row className="align-items-center">
                          <span>Rating:</span>
                        </Row>

                        <Row className="min-label-width align-items-center">
                          <div className="box green"></div>

                          <div className="legend-text">
                            A+ <span>|</span> AA <span>|</span> AA+{" "}
                            <span>|</span> AAA
                            <br />
                            (score 60-100)
                          </div>
                        </Row>

                        <Row className="min-label-width align-items-center">
                          <div className="box yellow"></div>
                          <div className="legend-text">
                            BB <span>|</span> BBB <span>|</span> A
                            <br />
                            (score 30-60)
                          </div>
                        </Row>

                        <Row className="min-label-width align-items-center">
                          <div className="box red"></div>
                          <div className="legend-text">
                            C <span>|</span> B
                            <br />
                            (score 0-30)
                          </div>
                        </Row>
                      </Row>
                    </Col>
                  </Row>
                ) : (
                  <Spinner animation="border" role="status" className="mt-5">
                    <span style={{ visibility: "hidden" }}>Loading...</span>
                  </Spinner>
                )}
              </Col>
              {/* secondo blocco destra */}

              <Col className="d-flex flex-column" style={{ minWidth: "400px" }}>
                {/*TODO*/}
                <Row className="ml-0">
                  <h5 className="title-simple-box text-uppercase mt-1 pl-0">
                    {translations
                      .filter((trans) => trans.code === "ICS127")
                      .map((trans) => trans.text)}
                  </h5>
                </Row>
                <Row className="d-flex justify-content-center align-items-center h-100">
                  <MetricsTable
                    data={sroiDetailRows}
                    columns={[
                      { key: "preMonitoring", label: "Score ex-ante" },
                      { key: "postMonitoring", label: "Score ex-post" },
                    ]}
                  />
                </Row>
              </Col>
            </Row>
          </Row>
        </div>
      )}

      {/*  //progetti */}
      <div className="simple-box scenaritab">
        <div className="row d-flex align-items-center mb-3">
          <div className="col-3">
            <h5 className="title-simple-box">Esplora progetti</h5>
          </div>
          <div className="col-9 text-right">
            <ButtonToolbar className="d-flex justify-content-end align-items-stretch gap-1">
              <OverlayTrigger placement="top" overlay={downloadTooltip}>
                <Button
                  variant="info"
                  onClick={() =>
                    exportCreatedMonitoringExcel(selectedMonitoring)
                  }
                  aria-label="Download Excel"
                  // className='ml-2 lineheight-initial'
                  className="lineheight-initial"
                  disabled={isLoading || !projects || projects.length === 0}
                >
                  <Row
                    className="align-items-center justify-content-center"
                    style={{ padding: "0.25rem 0.7rem" }}
                  >
                    {isLoading ? (
                      "Loading…"
                    ) : (
                      <DownloadFile width="23px" className="light-color m-0" />
                    )}
                  </Row>
                </Button>
              </OverlayTrigger>
              <InputGroup className="search-table align-items-center lineheight-initial ml-0">
                <FormControl
                  type="text"
                  id="filter-text-box"
                  placeholder="Search..."
                  className="d-none d-md-block "
                  onInput={(e) => onFilterTextBoxChanged(e)}
                />
                <Lens />
              </InputGroup>
            </ButtonToolbar>
          </div>
        </div>
        <div className="row">
          <div className="col-12" id="grid-wrapper">
            {/* Bug warning of aria-description is fixed in react v18 */}

            <div className="ag-theme-alpine projects-list-table">
              <AgGridReact
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={projects || []}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                tooltipShowDelay={0}
                tooltipHideDelay={2000}
                tooltipMouseTrack={true}
                onFirstDataRendered={onFirstDataRendered}
                //onVirtualRowRemoved={onVirtualRowRemoved}
                //onColumnResized={onColumnResized}
                //onGridSizeChanged={onGridSizeChanged}
                // onGridReady={onGridReady}
                // autoSizeStrategy={{ type: 'fitCellContents' }}
                onModelUpdated={onModelUpdated}
                pagination={true}
                paginationPageSize={20}
                headerHeight={50}
                rowHeight={50}
                localeText={localeText}
              />
              <div className="example-header">
                Progetti per pagina:&nbsp;
                <select onChange={onPageSizeChanged} id="page-size">
                  {optionsPagination}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Monitoring;
